import { CartAddEvent } from '@theme/events';

class MCProductBundle extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.variantImageOriginals ||= new WeakMap();
    this.applyVariantImages();
    this.variantImageObserver = new MutationObserver(() => this.applyVariantImages());
    this.variantImageObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-option-value', 'data-image-url', 'data-image-alt'],
    });
    this.abortController = new AbortController();
    this.productInfo = this.closest('.hdt-product-info__list');
    const formCandidate = document.getElementById(this.dataset.productFormId);
    this.form = formCandidate && this.productInfo?.contains(formCandidate) ? formCandidate : null;
    this.isFormOwner = Boolean(this.form && !this.form.dataset.mcBundleOwner);
    if (this.isFormOwner) this.form.dataset.mcBundleOwner = this.dataset.bundleId;
    this.offers = Array.from(this.querySelectorAll('[data-bundle-offer]'));
    this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
    this.buyNowButton = this.form?.querySelector('[data-mc-bundle-buy-now]');
    this.buyNowWrapper = this.form?.querySelector('[data-mc-bundle-buy-now-wrapper]');
    this.submitButtonWasDisabled = this.submitButton?.disabled;
    this.submitLabel = this.submitButton?.querySelector('.hdt-btn-atc_text');
    this.submitLabelSnapshot = this.submitLabel?.textContent;
    this.submitSpinner = this.submitButton?.querySelector('.hdt-loading__spinner');
    this.status = this.querySelector('[data-bundle-status]');
    this.priceBlock = this.productInfo?.querySelector('[data-mc-product-price]');
    this.buyButtonBlock = this.productInfo?.querySelector('[data-mc-product-buy-button]');
    this.quantityBlock = this.productInfo?.querySelector('.hdt-product-info__item--quantity');
    this.priceBlockSnapshot = this.priceBlock?.innerHTML;
    this.buyPriceSnapshot = this.buyButtonBlock?.querySelector('hdt-qty-price')?.outerHTML;
    this.quantityBlockWasHidden = this.quantityBlock?.hidden;
    this.quantityControlStates = Array.from(this.quantityBlock?.querySelectorAll('input, button, select') || [])
      .map((control) => ({ control, disabled: control.disabled }));

    const signal = this.abortController.signal;
    const radioName = `mc-bundle-offer-${this.dataset.bundleId}`;
    this.offers.forEach((offer) => {
      const input = offer.querySelector('[data-bundle-offer-input]');
      if (input) input.name = radioName;
    });

    this.addEventListener('change', this.handleChange.bind(this), { signal });
    this.addEventListener('keydown', this.handleKeydown.bind(this), { signal });
    if (this.isFormOwner) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this), { capture: true, signal });
      this.productInfo?.addEventListener('variant:update', this.handleNativeProductUpdate.bind(this), { signal });
      this.productInfo?.addEventListener('variant:updated', this.handleNativeProductUpdate.bind(this), { signal });
    } else {
      this.setAttribute('aria-disabled', 'true');
      this.querySelectorAll('input, select, button').forEach((control) => { control.disabled = true; });
    }

    if (Shopify.designMode) {
      document.addEventListener('shopify:block:select', this.handleEditorSelection.bind(this), { signal });
    }

    if (this.isFormOwner) this.initializeSelection();
  }

  disconnectedCallback() {
    this.variantImageObserver?.disconnect();
    this.abortController.abort();
    if (this.isFormOwner) {
      this.restoreProductUI();
      if (this.form?.dataset.mcBundleOwner === this.dataset.bundleId) {
        delete this.form.dataset.mcBundleOwner;
      }
    }
    this.initialized = false;
  }

  initializeSelection() {
    this.offers.forEach((offer) => {
      const hasLines = Boolean(offer.querySelector('[data-bundle-line]'))
        && !offer.querySelector('[data-bundle-unavailable]');
      const input = offer.querySelector('[data-bundle-offer-input]');
      offer.classList.toggle('is-unavailable', !hasLines);
      if (input) input.disabled = !hasLines;
    });

    this.offers.forEach((offer) => this.updateOfferPrice(offer));

    const availableOffers = this.offers.filter((offer) => !offer.classList.contains('is-unavailable'));
    const initialKey = this.dataset.initialOfferKey;
    const preferred = availableOffers.find((offer) => initialKey && offer.dataset.offerKey === initialKey)
      || availableOffers.find((offer) => offer.classList.contains('is-default'))
      || availableOffers.find((offer) => offer.querySelector('[data-bundle-offer-input]:checked'))
      || availableOffers[0];

    if (preferred) {
      this.selectOffer(preferred);
    } else {
      this.buyButtonBlock?.classList.add('is-mc-bundle-active');
      this.syncPurchaseButtons(false);
      if (this.buyNowWrapper) this.buyNowWrapper.hidden = false;
      if (this.buyNowButton) this.buyNowButton.disabled = true;
      this.setStatus(this.dataset.emptyMessage, true);
    }
  }

  applyVariantImages() {
    const images = new Map();
    this.querySelectorAll('[data-mc-variant-option-image]').forEach((entry) => {
      if (entry.closest('mc-product-bundle') !== this) return;
      if (entry.dataset.optionValue && entry.dataset.imageUrl) {
        images.set(entry.dataset.optionValue, entry.dataset);
      }
    });

    this.querySelectorAll('[data-mc-bundle-option-values]').forEach((card) => {
      if (card.closest('mc-product-bundle') !== this) return;
      let image = card.querySelector('.mc-bundle-item__variant-image');
      if (!this.variantImageOriginals.has(card)) {
        this.variantImageOriginals.set(card, image ? image.cloneNode(true) : null);
      }
      const override = card.dataset.mcBundleOptionValues.split('|')
        .map((value) => images.get(value)).find(Boolean);
      if (!override) {
        if (image?.hasAttribute('data-mc-image-override')) {
          const original = this.variantImageOriginals.get(card);
          if (original) image.replaceWith(original.cloneNode(true));
          else image.remove();
        }
        return;
      }
      if (!image) {
        image = document.createElement('img');
        image.className = 'mc-bundle-item__variant-image';
        image.width = 48;
        image.height = 48;
        image.loading = 'lazy';
        card.prepend(image);
      }
      image.setAttribute('data-mc-image-override', '');
      if (image.getAttribute('src') !== override.imageUrl) image.src = override.imageUrl;
      image.removeAttribute('srcset');
      image.alt = override.imageAlt || '';
    });
  }

  handleChange(event) {
    if (event.target.matches('[data-bundle-offer-input]')) {
      this.selectOffer(event.target.closest('[data-bundle-offer]'));
      return;
    }

    if (event.target.matches('[data-bundle-variant]')) {
      this.updateOfferPrice(this.activeOffer);
      this.syncProductUI();
      this.clearStatus();
    }
  }

  handleKeydown(event) {
    if (!event.target.matches('[data-bundle-offer-input]')) return;
    if (!['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'].includes(event.key)) return;

    event.preventDefault();
    const enabledInputs = this.offers
      .map((offer) => offer.querySelector('[data-bundle-offer-input]'))
      .filter((input) => input && !input.disabled);
    const currentIndex = enabledInputs.indexOf(event.target);
    const direction = ['ArrowDown', 'ArrowRight'].includes(event.key) ? 1 : -1;
    const nextInput = enabledInputs[(currentIndex + direction + enabledInputs.length) % enabledInputs.length];
    nextInput?.focus();
    nextInput?.click();
  }

  handleEditorSelection(event) {
    if (!this.isFormOwner) return;

    const offer = event.target.closest?.('[data-bundle-offer]');
    if (offer && this.contains(offer) && !offer.classList.contains('is-unavailable')) {
      this.selectOffer(offer);
    }
  }

  selectOffer(selectedOffer) {
    if (!selectedOffer) return;

    const shouldAnimate = Boolean(this.activeOffer && this.activeOffer !== selectedOffer);
    this.offers.forEach((offer) => {
      const isSelected = offer === selectedOffer;
      const wasSelected = offer === this.activeOffer;
      const input = offer.querySelector('[data-bundle-offer-input]');
      const details = offer.querySelector('[data-bundle-offer-details]');
      offer.classList.toggle('is-selected', isSelected);
      if (input) input.checked = isSelected;
      const hasOptions = Boolean(details?.querySelector('[data-bundle-slot]'));
      if (details) this.toggleOfferDetails(details, isSelected && hasOptions, hasOptions && shouldAnimate && (isSelected || wasSelected));
      details?.querySelectorAll('input, select, button').forEach((control) => {
        control.disabled = !isSelected || control.dataset.available === 'false';
      });
    });

    this.activeOffer = selectedOffer;
    if (this.submitButton) this.submitButton.disabled = false;
    this.clearStatus();
    this.syncProductUI();
  }

  toggleOfferDetails(details, isExpanded, shouldAnimate) {
    const currentHeight = details.hidden ? 0 : details.getBoundingClientRect().height;
    const currentOpacity = details.hidden ? 0 : Number.parseFloat(getComputedStyle(details).opacity);
    details.mcBundleAnimation?.cancel();
    details.mcBundleAnimation = null;
    details.setAttribute('aria-hidden', String(!isExpanded));

    if (!shouldAnimate || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      details.hidden = !isExpanded;
      details.style.removeProperty('overflow');
      return;
    }

    if (isExpanded) details.hidden = false;
    const targetHeight = isExpanded ? details.scrollHeight : 0;
    details.style.overflow = 'hidden';
    const animation = details.animate(
      [
        { height: `${currentHeight}px`, opacity: currentOpacity },
        { height: `${targetHeight}px`, opacity: isExpanded ? 1 : 0 },
      ],
      { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    );
    details.mcBundleAnimation = animation;

    animation.finished.then(() => {
      if (details.mcBundleAnimation !== animation) return;
      details.hidden = !isExpanded;
      details.style.removeProperty('overflow');
      details.mcBundleAnimation = null;
    }).catch(() => {});
  }

  getSelectedVariants(offer = this.activeOffer) {
    if (!offer) return [];

    const variants = [];
    offer.querySelectorAll('[data-bundle-line]').forEach((line) => {
      const hiddenVariant = line.querySelector('input[type="hidden"][data-bundle-variant]');
      if (hiddenVariant) {
        variants.push(hiddenVariant);
        return;
      }

      line.querySelectorAll('[data-bundle-slot]').forEach((slot) => {
        const selected = slot.querySelector('[data-bundle-variant]:checked');
        if (selected) variants.push(selected);
      });
    });

    return variants;
  }

  getOfferPricing(offer) {
    const variants = this.getSelectedVariants(offer);
    if (!variants.length) return null;

    const subtotal = variants.reduce((sum, variant) => {
      return sum + Number(variant.dataset.price || 0) * Number(variant.dataset.quantity || 1);
    }, 0);
    const percentValue = Number(offer.dataset.discountPercent || 0);
    const discountPercent = Number.isFinite(percentValue) ? Math.min(100, Math.max(0, percentValue)) : 0;
    const amountValue = Number(offer.dataset.discountAmount || 0);
    const fixedAmount = Number.isFinite(amountValue) ? Math.max(0, Math.round(amountValue)) : 0;
    const estimatedTotal = offer.dataset.discountType === 'fixed'
      ? Math.max(0, subtotal - fixedAmount)
      : Math.round(subtotal * (100 - discountPercent) / 100);

    return {
      subtotal,
      discountPercent,
      discountAmount: subtotal - estimatedTotal,
      estimatedTotal,
    };
  }

  updateOfferPrice(offer) {
    if (!offer) return;

    const pricing = this.getOfferPricing(offer);
    const pricingElement = offer.querySelector('[data-bundle-offer-pricing]');
    const priceElement = offer.querySelector('[data-bundle-offer-price]');
    const compareElement = offer.querySelector('[data-bundle-offer-compare-price]');
    if (!pricingElement || !priceElement || !compareElement) return;

    pricingElement.hidden = !pricing;
    if (!pricing) return;

    priceElement.textContent = this.formatMoney(pricing.estimatedTotal);
    compareElement.textContent = this.formatMoney(pricing.subtotal);
    priceElement.setAttribute('aria-label', `${this.dataset.finalPriceLabel}: ${priceElement.textContent}`);
    compareElement.setAttribute('aria-label', `${this.dataset.originalPriceLabel}: ${compareElement.textContent}`);
    compareElement.hidden = pricing.discountAmount <= 0;
  }

  buildItems() {
    const variants = this.getSelectedVariants();
    const itemMap = new Map();

    variants.forEach((variant) => {
      const id = Number(variant.value);
      const quantity = Number(variant.dataset.quantity || 1);
      const existing = itemMap.get(id);
      if (existing) {
        existing.quantity += quantity;
      } else {
        itemMap.set(id, {
          id,
          quantity,
          properties: {
            _mc_bundle: this.dataset.bundleId,
            _mc_bundle_offer: this.activeOffer.dataset.offerId,
          },
        });
      }
    });

    return Array.from(itemMap.values());
  }

  hasValidInventory(items, cart = null) {
    return items.every((item) => {
      const variants = Array.from(this.activeOffer.querySelectorAll(`[data-bundle-variant][value="${item.id}"]`));
      const representative = variants[0];
      if (!representative) return false;
      if (representative.dataset.available === 'false') return false;
      if (representative.dataset.inventoryManagement !== 'shopify') return true;
      if (representative.dataset.inventoryPolicy === 'continue') return true;
      const quantityInCart = cart?.items
        ?.filter((cartItem) => Number(cartItem.variant_id) === item.id)
        .reduce((sum, cartItem) => sum + cartItem.quantity, 0) || 0;
      return item.quantity + quantityInCart <= Number(representative.dataset.inventory || 0);
    });
  }

  syncProductUI() {
    const pricing = this.getOfferPricing(this.activeOffer);
    if (!pricing) {
      this.syncPurchaseButtons(false);
      return;
    }

    this.syncPurchaseButtons(this.hasValidInventory(this.buildItems()));
    if (this.buyNowWrapper) this.buyNowWrapper.hidden = false;

    this.updateOfferPrice(this.activeOffer);
    this.syncFormInputs();
    this.syncPriceBlock(pricing);
    this.syncBuyButton(pricing);
    this.syncQuantityBlock();
    document.dispatchEvent(new CustomEvent('currency:update'));
  }

  syncPurchaseButtons(available) {
    const disabled = !available || this.classList.contains('is-loading');
    if (this.submitButton) this.submitButton.disabled = disabled;
    if (this.buyNowButton) this.buyNowButton.disabled = disabled;
    if (this.submitLabel) {
      this.submitLabel.textContent = available ? this.dataset.addToCartLabel : this.dataset.soldOutLabel;
    }
  }

  syncFormInputs() {
    if (!this.form) return;

    this.form.querySelector(`[data-mc-bundle-form-values="${this.dataset.bundleId}"]`)?.remove();
    const container = document.createElement('div');
    container.hidden = true;
    container.dataset.mcBundleFormValues = this.dataset.bundleId;

    this.buildItems().forEach((item) => {
      const idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'items[][id]';
      idInput.value = item.id;
      const quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'items[][quantity]';
      quantityInput.value = item.quantity;
      container.append(idInput, quantityInput);
    });

    this.form.append(container);
  }

  syncPriceBlock(pricing) {
    if (!this.priceBlock) return;

    const saleList = this.priceBlock.querySelector('.hdt-price__sale');
    const noPickList = this.priceBlock.querySelector('.hdt-price__nopick');
    const price = saleList?.querySelector('hdt-price .hdt-money');
    const compare = saleList?.querySelector('hdt-compare-at-price');
    const compareMoney = compare?.querySelector('.hdt-money');
    if (!saleList || !price || !compare || !compareMoney) return;

    saleList.hidden = false;
    if (noPickList) noPickList.hidden = true;
    price.textContent = this.formatMoney(pricing.estimatedTotal);
    compareMoney.textContent = this.formatMoney(pricing.subtotal);
    compare.hidden = pricing.discountAmount <= 0;
    this.priceBlock.querySelector('hdt-unit-price')?.setAttribute('hidden', '');
    this.priceBlock.querySelectorAll('hdt-badge').forEach((badge) => badge.setAttribute('hidden', ''));
  }

  syncBuyButton(pricing) {
    if (!this.buyButtonBlock) return;

    this.buyButtonBlock.classList.add('is-mc-bundle-active');
    const quantityPrice = this.buyButtonBlock.querySelector('hdt-qty-price');
    const money = quantityPrice?.querySelector('.hdt-money');
    if (!quantityPrice || !money) return;

    quantityPrice.setAttribute('variant-price', pricing.estimatedTotal);
    quantityPrice.hidden = false;
    money.textContent = this.formatMoney(pricing.estimatedTotal);
  }

  syncQuantityBlock() {
    if (!this.quantityBlock) return;

    this.quantityBlock.hidden = true;
    this.quantityControlStates.forEach(({ control }) => { control.disabled = true; });
  }

  handleNativeProductUpdate() {
    queueMicrotask(() => {
      this.priceBlockSnapshot = this.priceBlock?.innerHTML;
      this.buyPriceSnapshot = this.buyButtonBlock?.querySelector('hdt-qty-price')?.outerHTML;
      this.syncProductUI();
    });
  }

  formatMoney(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(amount(?:_no_decimals)?(?:_with_comma_separator)?)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return `${(cents / 100).toFixed(2)}`;

    const token = match[1];
    const noDecimals = token.includes('no_decimals');
    const commaSeparator = token.includes('comma_separator');
    const value = cents / 100;
    const formatted = new Intl.NumberFormat(commaSeparator ? 'de-DE' : 'en-US', {
      minimumFractionDigits: noDecimals ? 0 : 2,
      maximumFractionDigits: noDecimals ? 0 : 2,
    }).format(value);

    return format.replace(placeholder, formatted).replace(/<[^>]*>/g, '');
  }

  async handleSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const buyNow = event.submitter === this.buyNowButton && Boolean(this.buyNowButton);
    if (!this.activeOffer) {
      this.setStatus(this.dataset.emptyMessage, true);
      return;
    }
    if (this.classList.contains('is-loading')) return;
    if (!this.form?.checkValidity()) {
      this.form?.reportValidity();
      return;
    }

    const items = this.buildItems();
    if (!items.length) {
      this.setStatus(this.dataset.selectionMessage, true);
      return;
    }

    this.setLoading(true);
    this.clearStatus();
    let navigatingToCheckout = false;

    try {
      const selectedOfferId = this.activeOffer.dataset.offerId;
      const cartDrawer = buyNow ? null : document.querySelector('hdt-cart-drawer[section-id]');
      const sectionId = cartDrawer?.getAttribute('section-id');
      const sectionPayload = sectionId
        ? { sections: [sectionId], sections_url: window.location.pathname }
        : {};
      const discountCode = this.activeOffer.dataset.discountCode?.trim();
      const cartSnapshot = await this.getCart();
      if (!this.hasValidInventory(items, cartSnapshot)) {
        this.setStatus(this.dataset.inventoryMessage, true);
        return;
      }
      const addData = await this.cartRequest('cart/add.js', { items, ...sectionPayload });
      let cartData = addData;

      if (discountCode) {
        try {
          const cartAfterAdd = await this.getCart();
          cartData = await this.cartRequest('cart/update.js', { discount: discountCode, ...sectionPayload });
          if (!this.isDiscountApplied(cartData, cartAfterAdd, discountCode)) {
            throw new Error(this.dataset.discountErrorMessage);
          }
        } catch (error) {
          console.error('MC bundle discount error:', error);
          try {
            await this.restoreCart(cartSnapshot, sectionPayload, selectedOfferId);
          } catch (rollbackError) {
            console.error('MC bundle cart recovery error:', rollbackError);
            document.dispatchEvent(new CustomEvent('cart:reload'));
            throw new Error(this.dataset.rollbackErrorMessage);
          }
          throw new Error(this.dataset.discountErrorMessage);
        }
      }

      if (buyNow) {
        window.location.assign(`${Shopify.routes.root}checkout`);
        navigatingToCheckout = true;
        return;
      }

      if (cartDrawer && sectionId) {
        document.dispatchEvent(new CartAddEvent(
          addData,
          this.id,
          cartData,
          'open_cart_drawer',
          'product-form-component'
        ));
      } else {
        window.location.assign(`${Shopify.routes.root}cart`);
        return;
      }

      this.setStatus(this.dataset.successMessage, false);
    } catch (error) {
      const message = error.status === 422
        ? this.dataset.inventoryMessage
        : error.message || this.dataset.errorMessage;
      this.setStatus(message, true);
    } finally {
      if (!navigatingToCheckout) this.setLoading(false);
    }
  }

  async cartRequest(path, body) {
    const response = await fetch(`${Shopify.routes.root}${path}`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.description || data.message || this.dataset.errorMessage);
      error.status = response.status;
      throw error;
    }

    return data;
  }

  async getCart() {
    const response = await fetch(`${Shopify.routes.root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(this.dataset.errorMessage);
    return response.json();
  }

  isDiscountApplied(cart, previousCart, discountCode) {
    if (this.activeOffer.dataset.discountValidation === 'checkout_only') return true;

    const normalizedCode = discountCode.toLowerCase();
    const discountCodeState = cart.discount_codes?.find((discount) => {
      return discount.code?.toLowerCase() === normalizedCode;
    });
    if (discountCodeState) return discountCodeState.applicable !== false;

    const applicationTitles = [
      ...(cart.cart_level_discount_applications || []).map((application) => application.title),
      ...(cart.items || []).flatMap((item) => {
        return (item.line_level_discount_allocations || []).map((allocation) => {
          return allocation.discount_application?.title;
        });
      }),
    ].filter(Boolean).map((title) => title.toLowerCase());

    if (applicationTitles.includes(normalizedCode)) return true;
    return Number(cart.total_discount || 0) > Number(previousCart.total_discount || 0);
  }

  async restoreCart(snapshot, sectionPayload, offerId) {
    if (!snapshot) throw new Error(this.dataset.rollbackErrorMessage);

    const currentCart = await this.getCart();
    const originalQuantities = new Map();
    snapshot.items.forEach((item) => {
      const signature = this.getLineSignature(item);
      originalQuantities.set(signature, (originalQuantities.get(signature) || 0) + item.quantity);
    });
    const updates = {};

    currentCart.items.forEach((item) => {
      const isCurrentBundle = String(item.properties?._mc_bundle) === String(this.dataset.bundleId)
        && String(item.properties?._mc_bundle_offer) === String(offerId);
      if (isCurrentBundle) {
        const signature = this.getLineSignature(item);
        updates[item.key] = originalQuantities.get(signature) || 0;
        originalQuantities.set(signature, 0);
      }
    });

    if (!Object.keys(updates).length) return currentCart;
    return this.cartRequest('cart/update.js', { updates, ...sectionPayload });
  }

  getLineSignature(item) {
    const properties = Object.entries(item.properties || {})
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
    return JSON.stringify([item.variant_id, properties]);
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    if (this.submitButton) {
      this.submitButton.setAttribute('aria-busy', String(isLoading));
    }
    if (this.buyNowButton) {
      this.buyNowButton.setAttribute('aria-busy', String(isLoading));
    }
    const items = this.activeOffer ? this.buildItems() : [];
    this.syncPurchaseButtons(items.length > 0 && this.hasValidInventory(items));
    if (this.submitSpinner) this.submitSpinner.hidden = !isLoading;
    this.setAttribute('aria-busy', String(isLoading));
    this.offers.forEach((offer) => {
      const offerInput = offer.querySelector('[data-bundle-offer-input]');
      const details = offer.querySelector('[data-bundle-offer-details]');
      if (offerInput) offerInput.disabled = isLoading || offer.classList.contains('is-unavailable');
      details?.querySelectorAll('input, select, button').forEach((control) => {
        control.disabled = isLoading || offer !== this.activeOffer || control.dataset.available === 'false';
      });
    });
  }

  setStatus(message, isError) {
    this.status.textContent = message || '';
    this.status.classList.toggle('is-error', isError);
  }

  clearStatus() {
    this.setStatus('', false);
  }

  restoreProductUI() {
    if (this.submitButton) this.submitButton.disabled = this.submitButtonWasDisabled;
    if (this.submitLabel && this.submitLabelSnapshot !== undefined) {
      this.submitLabel.textContent = this.submitLabelSnapshot;
    }
    if (this.buyNowWrapper) this.buyNowWrapper.hidden = true;
    if (this.buyNowButton) {
      this.buyNowButton.disabled = true;
      this.buyNowButton.removeAttribute('aria-busy');
    }
    this.form?.querySelector(`[data-mc-bundle-form-values="${this.dataset.bundleId}"]`)?.remove();
    if (this.priceBlock && this.priceBlockSnapshot !== undefined) {
      this.priceBlock.innerHTML = this.priceBlockSnapshot;
    }
    const quantityPrice = this.buyButtonBlock?.querySelector('hdt-qty-price');
    if (quantityPrice && this.buyPriceSnapshot) quantityPrice.outerHTML = this.buyPriceSnapshot;
    this.buyButtonBlock?.classList.remove('is-mc-bundle-active');
    if (this.quantityBlock) this.quantityBlock.hidden = this.quantityBlockWasHidden;
    this.quantityControlStates?.forEach(({ control, disabled }) => { control.disabled = disabled; });
  }
}

if (!customElements.get('mc-product-bundle')) {
  customElements.define('mc-product-bundle', MCProductBundle);
}
